import { useEffect } from 'react';
import { useLazyGetAttendeesQuery } from '~/store/services/schedule';
import { TreatmentProgramCode } from '../constants/scheduling.constants';

// In the case of an error fetching attendees, show the patient and their supports as non-selected attendees.
// This allows the user to still proceed with provider assignment, even if there's an issue with the attendees API.
// It also ensures that the patient and their supports are always visible as options in the UI.
const getFallbackAttendees = (patient: Patient): AttendeeWithSelection[] => {
  const patientAttendee: AttendeeWithSelection = {
    chosenName: patient.chosenName,
    externalId: patient.userId,
    firstName: patient.firstName,
    isRequired: false,
    isSelected: false,
    lastName: patient.lastName,
    userType: 'patient',
  };

  const supportAttendees: AttendeeWithSelection[] =
    patient.supports?.map((support) => ({
      chosenName: support.chosenName,
      externalId: support.externalId,
      firstName: support.firstName,
      isRequired: false,
      isSelected: false,
      lastName: support.lastName,
      userType: 'support',
    })) ?? [];

  return [patientAttendee, ...supportAttendees];
};

const getAttendeesFromResponse = (
  response: AttendeesResponse,
): AttendeeWithSelection[] =>
  (response.data ?? []).map((a) => ({
    ...a,
    isSelected: a.isRequired,
  }));

const getOrderedAttendees = (
  attendees: AttendeeWithSelection[],
): AttendeeWithSelection[] => {
  if (!attendees) return undefined;
  const required = attendees.filter((a) => a.isRequired);
  const optional = attendees.filter((a) => !a.isRequired);
  const ordered = [...required, ...optional];
  return ordered.map((a) => ({ ...a, isSelected: a.isRequired }));
};

export type AttendeeWithSelection = Attendee & {
  isSelected: boolean;
};

export interface UseGetAttendeesParams {
  appointmentType: Partial<AppointmentType>;
  onFetchAttendees: (attendees: AttendeeWithSelection[]) => void;
  patient: Patient;
  skip: boolean;
  treatmentProgram: TreatmentProgramCode;
}

export interface UseGetAttendeesResult {
  isFetching: boolean;
}

const useGetAttendees = ({
  appointmentType,
  onFetchAttendees,
  patient,
  skip,
  treatmentProgram,
}: UseGetAttendeesParams): UseGetAttendeesResult => {
  const shouldFetch = Boolean(
    !skip &&
      patient?.userId &&
      appointmentType?.appointmentGroup &&
      treatmentProgram,
  );

  const [getAttendees, { isFetching }] = useLazyGetAttendeesQuery();

  useEffect(() => {
    if (shouldFetch) {
      (async () => {
        const result = await getAttendees(
          {
            appointmentTypeGroup: appointmentType.appointmentGroup,
            patientId: patient.userId,
            treatmentProgram,
          },
          true,
        );

        // If there's an error or no attendees are returned, fall back to showing the patient and their supports as non-selected attendees
        const attendees =
          result.isError || (result.data?.data ?? []).length === 0
            ? getFallbackAttendees(patient)
            : getAttendeesFromResponse(result.data);

        onFetchAttendees(getOrderedAttendees(attendees));
      })();
    }
  }, [
    getAttendees,
    appointmentType?.appointmentGroup,
    onFetchAttendees,
    patient,
    shouldFetch,
    treatmentProgram,
  ]);

  return { isFetching };
};

export default useGetAttendees;
